import {token} from 'brandi';

import {NullStorage, RealStorage} from '@specials/localstorage';
import {ProjectUserAuth} from '@specials/project-user';
import {SentryBrowserAbstract} from '@specials/sentry-browser';

import {ResourcesAll} from '@/resources';
import {AssetsLoader} from '@/services/AssetsLoader/AssetsLoader';
import {CacheService} from '@/services/CacheService/CacheService';
import {CacheStorageAbstract} from '@/services/CacheService/CacheStorage/CacheStorageAbstract';
import {DateTimeService} from '@/services/DateTimeService/DateTimeService';
import {DevToolsMbService} from '@/services/DevToolsMbService/DevToolsMbService';
import {ErrorService} from '@/services/ErrorService/ErrorService';
import {LoggerService} from '@/services/LoggerService/LoggerService';
import {NavigationService} from '@/services/NavigationService/NavigationService';
import {PlatformService} from '@/services/PlatformService/PlatformService';
import {ProjectApiHeaders} from '@/services/ProjectApi/ProjectApiHeaders';
import {HttpClientProjectApi} from '@/services/ProjectApi/ProjectApiHttpClient';
import {IProjectApi} from '@/services/ProjectApi/types';
import {ProjectAuthApi} from '@/services/ProjectAuthApi/ProjectAuthApi';
import {ProjectUserInfoData, ProjectUserLoginData} from '@/services/ProjectAuthApi/types';
import {QueryStringService} from '@/services/QueryStringService/QueryStringService';
import {ScrollService} from '@/services/ScrollService/ScrollService';
import {ShareService} from '@/services/ShareService/ShareService';
import {TrackingService} from '@/services/TrackingService/TrackingService';
import {WindowVisibilityService} from '@/services/WindowVisibilityService/WindowVisibilityService';
import {AuthStore} from '@/stores/AuthStore/AuthStore';
import {BonusesStore} from '@/stores/BonusesStore/BonusesStore';
import {ImageLoaderStore} from '@/stores/ImageLoaderStore/ImageLoaderStore';
import {ItemModalContentStore} from '@/stores/ItemModalContentStore/ItemModalContentStore';
import {ItemsStore} from '@/stores/ItemsStore/ItemsStore';
import {ModalStore} from '@/stores/ModalStore/ModalStore';
import {OnboardingStore} from '@/stores/OnboardingStore/OnboardingStore';
import {ShareStore} from '@/stores/ShareStore/ShareStore';
import {TimerStore} from '@/stores/TimerStore/TimerStore';
import {UserItemsStore} from '@/stores/UserItemsStore/UserItemsStore';
import {UserEnergyStore} from '@/stores/UserStores/UserEnergyStore';
import {UserStore} from '@/stores/UserStores/UserStore';
import {ViewportStore} from '@/stores/ViewportStore/ViewportStore';
import {ApiRoutes, ClientRoutes, Config, Envs, GlobalWindow} from '@/types';
import {TitleAnimation} from '@/ui/components/TitleAnimation/Animation';
import {ErrorActionHandlerUseCase} from '@/useCases/ErrorActionHandlerUseCase/ErrorActionHandlerUseCase';
import {InitializeUseCase} from '@/useCases/InitializeUseCase/InitializeUseCase';
import {OnboardingFinishUseCase} from '@/useCases/OnboardingFinishUseCase/OnboardingFinishUseCase';
import {ShareUseCase} from '@/useCases/ShareUseCase/ShareUseCase';
import {SubscriptionChangeUseCase} from '@/useCases/SubscriptionChangeUseCase/SubscriptionChangeUseCase';
import {BonusesInfoSharedUseCase} from '@/useCases/shared/BonusesInfoSharedUseCase/BonusesInfoSharedUseCase';
import {ItemContentSharedUseCase} from '@/useCases/shared/ItemContentSharedUseCase/ItemContentSharedUseCase';
import {ModalControlSharedUseCase} from '@/useCases/shared/ModalControlSharedUseCase/ModalControlSharedUseCase';
import {OpenItemSharedUseCase} from '@/useCases/shared/OpenItemSharedUseCase/OpenItemSharedUseCase';
import {TimerUpdateSharedUseCase} from '@/useCases/shared/TimerUpdateSharedUseCase/TimerUpdateSharedUseCase';
import {CookiesService} from "@/services/CookiesService/CookiesService";

export const DI_TOKENS = {
  resources: token<ResourcesAll>('resources'),
  envs: token<Envs>('envs'),
  config: token<Config>('config'),
  clientRoutes: token<ClientRoutes>('clientRoutes'),
  apiRoutes: token<ApiRoutes>('apiRoutes'),
  scrollService: token<ScrollService>('scrollService'),
  navigationService: token<NavigationService>('navigationService'),
  httpClientProjectApi: token<HttpClientProjectApi>('httpClientProjectApi'),
  globalWindow: token<GlobalWindow>('globalWindow'),
  sentryBrowser: token<SentryBrowserAbstract>('sentryBrowser'),
  loggerService: token<LoggerService>('loggerService'),
  trackingService: token<TrackingService>('trackingService'),
  localStorageService: token<RealStorage | NullStorage>('localStorageService'),
  projectUserAuth: token<ProjectUserAuth<ProjectUserInfoData, ProjectUserLoginData>>('projectUserAuth'),
  queryStringService: token<QueryStringService>('queryStringService'),
  platformService: token<PlatformService>('platformService'),
  errorService: token<ErrorService>('errorService'),
  shareService: token<ShareService>('shareService'),
  dateTimeService: token<DateTimeService>('dateTimeService'),
  cacheStorage: token<CacheStorageAbstract>('cacheStorage'),
  cacheService: token<CacheService>('cacheService'),
  titleAnimation: token<TitleAnimation>('titleAnimation'),
  assetsLoader: token<AssetsLoader>('assetsLoader'),
  devtoolsMbService: token<DevToolsMbService>('devtoolsMbService'),
  windowVisibilityService: token<WindowVisibilityService>('windowVisibilityService'),
  cookiesService: token<CookiesService>('cookiesService'),
  // api
  projectApi: token<IProjectApi>('projectApi'),
  projectAuthApi: token<ProjectAuthApi>('projectAuthApi'),
  projectApiHeaders: token<ProjectApiHeaders>('projectApiHeaders'),
  // stores
  userStore: token<UserStore>('userStore'),
  userEnergyStore: token<UserEnergyStore>('userEnergyStore'),
  authStore: token<AuthStore>('authStore'),
  modalStore: token<ModalStore>('modalStore'),
  timerStore: token<TimerStore>('timerStore'),
  shareStore: token<ShareStore>('shareStore'),
  bonusesStore: token<BonusesStore>('bonusesStore'),
  itemsStore: token<ItemsStore>('itemsStore'),
  userItemsStore: token<UserItemsStore>('userItemsStore'),
  itemModalContentStore: token<ItemModalContentStore>('itemModalContentStore'),
  onboardingStore: token<OnboardingStore>('onboardingStore'),
  viewportStore: token<ViewportStore>('viewportStore'),
  imageLoaderStore: token<ImageLoaderStore>('imageLoaderStore'),
  // useCases
  onboardingFinishUseCase: token<OnboardingFinishUseCase>('onboardingFinishUseCase'),
  subscriptionChangeUseCase: token<SubscriptionChangeUseCase>('subscriptionChangeUseCase'),
  timerUpdateSharedUseCase: token<TimerUpdateSharedUseCase>('timerUpdateSharedUseCase'),
  shareUseCase: token<ShareUseCase>('shareUseCase'),
  initializeUseCase: token<InitializeUseCase>('initializeUseCase'),
  modalControlSharedUseCase: token<ModalControlSharedUseCase>('modalControlSharedUseCase'),
  bonusesInfoSharedUseCase: token<BonusesInfoSharedUseCase>('bonusesInfoSharedUseCase'),
  errorActionHandlerUseCase: token<ErrorActionHandlerUseCase>('errorActionHandlerUseCase'),
  itemContentSharedUseCase: token<ItemContentSharedUseCase>('itemContentSharedUseCase'),
  openItemSharedUseCase: token<OpenItemSharedUseCase>('openItemSharedUseCase'),
};
